import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SlidersHorizontal, X, ChevronDown, Grid3X3, List, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANDS = ['Bosch', 'DeWalt', 'Makita', 'Milwaukee', 'Hikoki', 'Stanley', 'Black+Decker', 'Total', 'Ingco'];
const CATEGORIES = ['Drills & Drivers', 'Angle Grinders', 'Rotary Hammers', 'Circular Saws', 'Jig Saws', 'Sanders', 'Impact Wrenches', 'Heat Guns', 'Air Blowers', 'Pressure Washers', 'Batteries & Chargers', 'Accessories'];
const POWER_TYPES = ['corded', 'cordless'];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Best Rated' },
  { value: 'popular', label: 'Most Popular' },
];

export default function ProductListingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    brand: searchParams.get('brand') || '',
    power_type: searchParams.get('power_type') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || '',
    search: searchParams.get('search') || '',
    sort: searchParams.get('sort') || 'newest',
    page: parseInt(searchParams.get('page') || '1'),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const newFilters = {
      category: searchParams.get('category') || '',
      brand: searchParams.get('brand') || '',
      power_type: searchParams.get('power_type') || '',
      min_price: searchParams.get('min_price') || '',
      max_price: searchParams.get('max_price') || '',
      search: searchParams.get('search') || '',
      sort: searchParams.get('sort') || 'newest',
      page: parseInt(searchParams.get('page') || '1'),
    };
    setFilters(newFilters);
    fetchProducts(newFilters);
  }, [searchParams]);

  const fetchProducts = async (f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (f.category) params.set('category', f.category);
      if (f.brand) params.set('brand', f.brand);
      if (f.power_type) params.set('power_type', f.power_type);
      if (f.min_price) params.set('min_price', f.min_price);
      if (f.max_price) params.set('max_price', f.max_price);
      if (f.search) params.set('search', f.search);
      params.set('sort', f.sort);
      params.set('page', f.page);
      params.set('limit', '20');
      const res = await axios.get(`${API}/products?${params}`);
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearAll = () => {
    setSearchParams({});
  };

  const activeFiltersCount = [filters.category, filters.brand, filters.power_type, filters.min_price, filters.max_price].filter(Boolean).length;

  const pageTitle = filters.category || filters.brand
    ? (filters.category || `${filters.brand} Tools`)
    : filters.search ? `Search: "${filters.search}"` : 'All Products';

  return (
    <div className="bg-background min-h-screen">
      <Header />
      <div className="pt-[105px]">
        {/* Page header */}
        <div className="bg-surface border-b border-white/5 py-5">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-heading font-black text-2xl md:text-3xl uppercase text-white">{pageTitle}</h1>
                <p className="text-muted-foreground text-sm mt-0.5">
                  {loading ? 'Loading...' : `${total} product${total !== 1 ? 's' : ''} found`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Sort */}
                <select
                  value={filters.sort}
                  onChange={e => applyFilter('sort', e.target.value)}
                  className="bg-surface border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-primary transition-colors"
                  style={{ borderRadius: 0 }}
                  data-testid="sort-select"
                >
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {/* Mobile filter toggle */}
                <button
                  className="md:hidden flex items-center gap-1.5 bg-surface border border-white/10 hover:border-primary text-white text-xs px-3 py-2 transition-colors"
                  onClick={() => setSidebarOpen(true)}
                  data-testid="mobile-filter-btn"
                >
                  <SlidersHorizontal size={13} /> Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-6">
          <div className="flex gap-6">
            {/* Sidebar filters */}
            <aside className={`
              ${sidebarOpen ? 'fixed inset-0 z-50 flex' : 'hidden md:block'}
              md:relative md:w-56 lg:w-64 flex-shrink-0
            `}>
              {/* Mobile overlay */}
              {sidebarOpen && <div className="md:hidden fixed inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />}

              <div className={`
                ${sidebarOpen ? 'relative z-10 w-72 bg-surface overflow-y-auto h-full' : ''}
                md:sticky md:top-24 md:max-h-screen md:overflow-y-auto
              `}>
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <span className="font-heading font-bold text-sm uppercase tracking-widest text-white">Filters</span>
                  <div className="flex items-center gap-2">
                    {activeFiltersCount > 0 && (
                      <button onClick={clearAll} className="text-xs text-secondary hover:text-white transition-colors" data-testid="clear-filters-btn">
                        Clear all
                      </button>
                    )}
                    {sidebarOpen && <button onClick={() => setSidebarOpen(false)}><X size={16} className="text-white/60" /></button>}
                  </div>
                </div>

                {/* Active filters */}
                {activeFiltersCount > 0 && (
                  <div className="p-3 border-b border-white/10">
                    <div className="flex flex-wrap gap-1.5">
                      {filters.category && (
                        <button onClick={() => applyFilter('category', '')} className="flex items-center gap-1 text-xs bg-primary/10 border border-primary/30 text-primary px-2 py-1 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400 transition-colors">
                          {filters.category} <X size={10} />
                        </button>
                      )}
                      {filters.brand && (
                        <button onClick={() => applyFilter('brand', '')} className="flex items-center gap-1 text-xs bg-primary/10 border border-primary/30 text-primary px-2 py-1 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400 transition-colors">
                          {filters.brand} <X size={10} />
                        </button>
                      )}
                      {filters.power_type && (
                        <button onClick={() => applyFilter('power_type', '')} className="flex items-center gap-1 text-xs bg-primary/10 border border-primary/30 text-primary px-2 py-1 hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-400 transition-colors">
                          {filters.power_type} <X size={10} />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="p-4 space-y-5">
                  {/* Search */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Search</p>
                    <form onSubmit={e => { e.preventDefault(); applyFilter('search', e.target.search.value); }} className="relative">
                      <input name="search" defaultValue={filters.search} placeholder="Tool name, model..." className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 text-xs px-3 py-2 pr-8 outline-none transition-colors" style={{ borderRadius: 0 }} data-testid="sidebar-search-input" />
                      <button type="submit" className="absolute right-0 top-0 bottom-0 px-2 text-white/30"><Search size={12} /></button>
                    </form>
                  </div>

                  {/* Categories */}
                  <FilterSection title="Category">
                    {CATEGORIES.map(cat => (
                      <FilterItem key={cat} label={cat} active={filters.category === cat}
                        onClick={() => applyFilter('category', filters.category === cat ? '' : cat)} />
                    ))}
                  </FilterSection>

                  {/* Brands */}
                  <FilterSection title="Brand">
                    {BRANDS.map(b => (
                      <FilterItem key={b} label={b} active={filters.brand === b}
                        onClick={() => applyFilter('brand', filters.brand === b ? '' : b)} />
                    ))}
                  </FilterSection>

                  {/* Power Type */}
                  <FilterSection title="Power Type">
                    {POWER_TYPES.map(t => (
                      <FilterItem key={t} label={t.charAt(0).toUpperCase() + t.slice(1)} active={filters.power_type === t}
                        onClick={() => applyFilter('power_type', filters.power_type === t ? '' : t)} />
                    ))}
                  </FilterSection>

                  {/* Price Range */}
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Price Range (AED)</p>
                    <div className="flex gap-2">
                      <input type="number" placeholder="Min" defaultValue={filters.min_price}
                        onBlur={e => applyFilter('min_price', e.target.value)} className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 text-xs px-2 py-2 outline-none" style={{ borderRadius: 0 }} data-testid="min-price-input" />
                      <input type="number" placeholder="Max" defaultValue={filters.max_price}
                        onBlur={e => applyFilter('max_price', e.target.value)} className="w-full bg-background border border-white/10 focus:border-primary text-white placeholder-white/30 text-xs px-2 py-2 outline-none" style={{ borderRadius: 0 }} data-testid="max-price-input" />
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Products grid */}
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Array(12).fill(0).map((_, i) => (
                    <div key={i} className="aspect-square shimmer-bg" />
                  ))}
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-20">
                  <div className="text-4xl mb-4">🔧</div>
                  <h3 className="font-heading font-bold text-xl text-white mb-2">No Products Found</h3>
                  <p className="text-muted-foreground text-sm mb-4">Try adjusting your filters or search terms</p>
                  <button onClick={clearAll} className="btn-primary text-sm py-2 px-6" data-testid="no-results-clear-btn">Clear Filters</button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
                    {products.map(p => <ProductCard key={p.id} product={p} />)}
                  </div>

                  {/* Pagination */}
                  {pages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-8">
                      {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                        <button
                          key={p}
                          onClick={() => applyFilter('page', p.toString())}
                          className={`w-9 h-9 text-sm font-bold transition-colors ${filters.page === p ? 'bg-primary text-black' : 'bg-surface border border-white/10 text-white hover:border-primary hover:text-primary'}`}
                          data-testid={`page-btn-${p}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function FilterSection({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button className="flex items-center justify-between w-full text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2 hover:text-primary transition-colors" onClick={() => setOpen(!open)}>
        {title} <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="space-y-0.5">{children}</div>}
    </div>
  );
}

function FilterItem({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs transition-colors text-left ${active ? 'text-primary bg-primary/10 font-bold' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
    >
      <span className={`w-3 h-3 border flex-shrink-0 flex items-center justify-center ${active ? 'bg-primary border-primary' : 'border-white/20'}`}>
        {active && <span className="w-1.5 h-1.5 bg-black" />}
      </span>
      {label}
    </button>
  );
}
